<template>
  <div class="news-publish">
    <el-card>
      <template #header>
        <div class="card-header">
          <h2>{{ isEdit ? '编辑新闻' : '发布新闻' }}</h2>
          <p>仅理事会员可发布新闻</p>
        </div>
      </template>

      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
        size="large"
      >
        <el-form-item label="新闻标题" prop="title">
          <el-input
            v-model="form.title"
            placeholder="请输入新闻标题"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="新闻摘要" prop="summary">
          <el-input
            v-model="form.summary"
            type="textarea"
            :rows="3"
            placeholder="请输入新闻摘要"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>

        <el-form-item label="封面图片" prop="cover">
          <FileUploader
            v-model="form.coverFiles"
            upload-type="image"
            list-type="picture-card"
            :limit="1"
            custom-tip="建议尺寸：800x450，支持JPG、PNG格式"
          />
        </el-form-item>

        <el-form-item label="新闻内容" prop="content">
          <Editor
            v-model="form.content"
            :height="500"
          />
        </el-form-item>

        <el-form-item label="新闻设置">
          <el-checkbox v-model="form.isTop">置顶</el-checkbox>
          <el-checkbox v-model="form.isHot">热门</el-checkbox>
          <el-checkbox v-model="form.isRecommend">推荐</el-checkbox>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            :loading="submitting"
            @click="handleSubmit(false)"
          >
            {{ submitting ? '提交中...' : '提交审核' }}
          </el-button>
          <el-button
            :loading="submitting"
            @click="handleSubmit(true)"
          >
            保存草稿
          </el-button>
          <el-button @click="handleCancel">取消</el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import newsApi from '@/api/news'
import FileUploader from '@/components/FileUploader.vue'
import Editor from '@/components/editor.vue'

export default {
  name: 'NewsPublish',
  
  components: {
    FileUploader,
    Editor
  },
  
  data() {
    return {
      form: {
        title: '',
        summary: '',
        content: '',
        coverFiles: [],
        isTop: false,
        isHot: false,
        isRecommend: false
      },
      rules: {
        title: [
          { required: true, message: '请输入新闻标题', trigger: 'blur' },
          { min: 5, message: '标题至少5个字', trigger: 'blur' }
        ],
        summary: [
          { required: true, message: '请输入新闻摘要', trigger: 'blur' },
          { min: 10, message: '摘要至少10个字', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入新闻内容', trigger: 'blur' },
          { min: 50, message: '内容至少50个字', trigger: 'blur' }
        ]
      },
      submitting: false,
      isEdit: false,
      newsId: null
    }
  },
  
  computed: {
    ...mapGetters('auth', ['isDirector'])
  },
  
  mounted() {
    // 检查是否为理事会员
    if (!this.isDirector) {
      this.$message.error('仅理事会员可发布新闻')
      this.$router.push('/user/home')
      return
    }
    
    // 检查是否为编辑模式
    this.newsId = this.$route.query.id
    if (this.newsId) {
      this.isEdit = true
      this.loadNewsDetail()
    }
  },
  
  methods: {
    /**
     * 加载新闻详情（编辑模式）
     */
    async loadNewsDetail() {
      try {
        const res = await newsApi.getMyNewsDetail(this.newsId)
        
        if (res && res.code === 2000) {
          const data = res.data
          this.form = {
            title: data.title,
            summary: data.summary,
            content: data.content,
            coverFiles: data.cover ? [{ url: data.cover }] : [],
            isTop: data.is_top === 1,
            isHot: data.is_hot === 1,
            isRecommend: data.is_recommend === 1
          }
        }
      } catch (error) {
        console.error('加载新闻详情失败:', error)
      }
    },
    
    /**
     * 提交新闻
     */
    async handleSubmit(isDraft) {
      // 如果不是草稿，验证表单
      if (!isDraft) {
        const valid = await this.$refs.formRef.validate().catch(() => false)
        if (!valid) return
      }
      
      this.submitting = true
      
      try {
        const submitData = {
          title: this.form.title,
          summary: this.form.summary,
          content: this.form.content,
          cover: this.form.coverFiles[0]?.url || '',
          is_top: this.form.isTop ? 1 : 0,
          is_hot: this.form.isHot ? 1 : 0,
          is_recommend: this.form.isRecommend ? 1 : 0,
          is_draft: isDraft ? 1 : 0
        }
        
        let res
        if (this.isEdit) {
          res = await newsApi.updateNews(this.newsId, submitData)
        } else {
          res = await newsApi.publishNews(submitData)
        }
        
        if (res && res.code === 2000) {
          this.$message.success(isDraft ? '保存成功' : '提交成功，等待审核')
          
          // 跳转到我的新闻列表
          setTimeout(() => {
            this.$router.push('/news/my-list')
          }, 1500)
        }
      } catch (error) {
        console.error('提交新闻失败:', error)
      } finally {
        this.submitting = false
      }
    },
    
    /**
     * 取消
     */
    handleCancel() {
      this.$router.back()
    }
  }
}
</script>

<style lang="scss" scoped>
.news-publish {
  min-height: 100vh;
  padding: 20px;
  background-color: #f5f7fa;
  
  .el-card {
    max-width: 1000px;
    margin: 0 auto;
    
    .card-header {
      text-align: center;
      
      h2 {
        margin: 0 0 10px 0;
        font-size: 24px;
        color: #303133;
      }
      
      p {
        margin: 0;
        color: #909399;
        font-size: 14px;
      }
    }
  }
  
  :deep(.el-checkbox) {
    margin-right: 20px;
  }
}

@media (max-width: 768px) {
  .news-publish {
    padding: 10px;
  }
}
</style>
